import { useEffect, useRef, useState } from "react";
import { X, Camera, AlertCircle, ShieldCheck, Mail, CheckCircle2 } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

/**
 * PersonalisationModal — concierge "send us a photo, we'll hand-craft your
 * proof" flow. No AI runs here; the customer's photo lands in the admin
 * queue and the admin emails them a link to /preview/:id when the proof
 * is ready.
 *
 * Steps:
 *   1. "form"     — customer enters email + photo + (optional) notes
 *   2. "submitting" — POSTing to /personalisation/start
 *   3. "submitted"  — confirmation screen with what happens next
 */
export default function PersonalisationModal({
  open,
  onClose,
  personalisable,
}) {
  const [step, setStep] = useState("form");
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [instructions, setInstructions] = useState("");
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setStep("form");
      setFile(null);
      setFilePreview(null);
      setEmail("");
      setName("");
      setInstructions("");
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!file) {
      setFilePreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setFilePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Lock background scroll while modal is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  if (!open || !personalisable) return null;

  const handlePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setError("That's not an image. Please pick a JPG, PNG or HEIC photo.");
      return;
    }
    if (f.size > 12 * 1024 * 1024) {
      setError("Photo too big (max 12MB). Try a smaller export.");
      return;
    }
    setError(null);
    setFile(f);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!file) {
      setError("Pick a photo first.");
      return;
    }
    if (!email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError("Enter a valid email so we can send your proof.");
      return;
    }
    setError(null);
    setStep("submitting");
    const fd = new FormData();
    fd.append("shopify_product_id", personalisable.shopify_product_id);
    fd.append("customer_email", email.trim());
    if (name.trim()) fd.append("customer_name", name.trim());
    if (instructions.trim()) fd.append("custom_instructions", instructions.trim());
    fd.append("face_photo", file);
    try {
      await api.post("/personalisation/start", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setStep("submitted");
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        "Couldn't submit your request. Try again in a moment?";
      setError(msg);
      setStep("form");
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Personalise your tee"
      className="fixed inset-0 z-[80] flex items-stretch justify-center overflow-y-auto bg-black/70 backdrop-blur-sm md:items-center md:p-6"
      data-testid="personalisation-modal"
      onClick={onClose}
    >
      <div
        className="relative flex w-full max-w-3xl flex-col bg-[#F9F9F4] text-[#1A3626] shadow-2xl md:max-h-[92vh] md:rounded-none md:border-2 md:border-[#1A3626]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 rounded-full bg-[#F9F9F4]/90 p-2 text-[#1A3626] transition hover:bg-[#1A3626] hover:text-[#F9F9F4]"
          data-testid="personalisation-close"
        >
          <X className="h-5 w-5" strokeWidth={2.5} />
        </button>

        <div className="overflow-y-auto px-5 pb-8 pt-8 md:px-10 md:pt-10">
          {/* STEP: FORM */}
          {step === "form" && (
            <>
              <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#93C065] md:text-xs">
                Personalisation Studio
              </div>
              <h2
                className="font-display mt-2 text-2xl font-black uppercase leading-[0.95] tracking-tight sm:text-3xl md:text-4xl"
                data-testid="personalisation-title"
              >
                Upload your mate.
                <br />
                We hand-craft the rest.
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-[#1A3626]/80 md:text-[15px]">
                Send us a clear photo of the face you&rsquo;d like immortalised
                — our team will hand-craft your proof and email it to you,
                usually within <span className="font-display font-black">24 hours</span>.
                Nothing&rsquo;s charged until you&rsquo;ve seen it and approved.
              </p>

              <form onSubmit={submit} className="mt-7 space-y-5" data-testid="step-form">
                {/* Reference design preview */}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-[140px_1fr] sm:items-center">
                  <img
                    src={fileUrl(personalisable.base_design_url)}
                    alt={personalisable.title}
                    className="h-32 w-full border-2 border-[#1A3626] object-contain sm:h-32 sm:w-32"
                  />
                  <div>
                    <div className="font-display text-[10px] font-bold uppercase tracking-[0.3em] text-[#1A3626]/65">
                      The Design
                    </div>
                    <div className="font-display mt-1 text-lg font-black uppercase leading-tight md:text-xl">
                      {personalisable.title}
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-[#1A3626]/75 md:text-sm">
                      {personalisable.upload_instructions}
                    </p>
                  </div>
                </div>

                {/* Photo upload */}
                <label
                  htmlFor="wc-face-input"
                  className="flex cursor-pointer flex-col items-center justify-center gap-3 border-2 border-dashed border-[#1A3626] bg-white p-7 text-center transition hover:bg-[#EBE7DD] md:p-10"
                  data-testid="face-upload-area"
                >
                  {filePreview ? (
                    <>
                      <img
                        src={filePreview}
                        alt="Your selected face"
                        className="h-32 w-32 rounded-full border-2 border-[#1A3626] object-cover"
                      />
                      <div className="font-display text-xs font-bold uppercase tracking-[0.28em]">
                        Tap to change photo
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#1A3626] text-[#F9F9F4]">
                        <Camera className="h-6 w-6" strokeWidth={2.5} />
                      </div>
                      <div className="font-display text-sm font-black uppercase tracking-[0.2em] md:text-base">
                        Tap to upload photo
                      </div>
                      <div className="text-xs text-[#1A3626]/70 md:text-sm">
                        JPG, PNG or HEIC · up to 12MB
                      </div>
                    </>
                  )}
                  <input
                    id="wc-face-input"
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePick}
                    className="hidden"
                    data-testid="face-upload-input"
                  />
                </label>

                {/* Customer email + name */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="wc-email"
                      className="font-display text-[10px] font-bold uppercase tracking-[0.3em] text-[#1A3626]/75"
                    >
                      Your email *
                    </label>
                    <input
                      id="wc-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="mt-2 w-full border-2 border-[#1A3626] bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#93C065]"
                      data-testid="customer-email"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="wc-name"
                      className="font-display text-[10px] font-bold uppercase tracking-[0.3em] text-[#1A3626]/75"
                    >
                      Your name (optional)
                    </label>
                    <input
                      id="wc-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Sandra"
                      className="mt-2 w-full border-2 border-[#1A3626] bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#93C065]"
                      data-testid="customer-name"
                    />
                  </div>
                </div>

                {/* Custom notes */}
                <div>
                  <label className="font-display text-[10px] font-bold uppercase tracking-[0.3em] text-[#1A3626]/75">
                    Anything else we should know? (optional)
                  </label>
                  <textarea
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    rows={3}
                    placeholder="e.g. This is for my Dad's 60th. He always wears a flat cap."
                    className="mt-2 w-full border-2 border-[#1A3626] bg-white p-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#93C065]"
                    data-testid="face-instructions"
                  />
                </div>

                {error && (
                  <div
                    className="flex items-start gap-2 border-2 border-red-700 bg-red-50 px-4 py-3 text-sm text-red-800"
                    data-testid="upload-error"
                  >
                    <AlertCircle
                      className="mt-0.5 h-4 w-4 shrink-0"
                      strokeWidth={2.5}
                    />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!file || !email.trim()}
                  className="wc-btn flex w-full items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="submit-personalisation-btn"
                >
                  <Mail className="h-4 w-4" strokeWidth={2.8} />
                  Send for hand-crafted proof
                </button>

                <div className="flex items-start gap-2 text-[11px] uppercase tracking-[0.25em] text-[#1A3626]/60">
                  <ShieldCheck
                    className="mt-0.5 h-3.5 w-3.5 shrink-0"
                    strokeWidth={2.5}
                  />
                  <span>
                    No payment yet. We email you a watermarked proof — you only
                    pay if you love it.
                  </span>
                </div>
              </form>
            </>
          )}

          {/* STEP: SUBMITTING */}
          {step === "submitting" && (
            <div
              className="mt-2 flex flex-col items-center justify-center gap-5 border-2 border-[#1A3626] bg-white p-10 text-center"
              data-testid="step-submitting"
            >
              <div className="relative h-14 w-14">
                <div className="absolute inset-0 rounded-full border-4 border-[#EBE7DD]" />
                <div className="absolute inset-0 animate-spin rounded-full border-4 border-transparent border-t-[#1A3626]" />
              </div>
              <div className="font-display text-base font-black uppercase tracking-tight">
                Sending your request…
              </div>
            </div>
          )}

          {/* STEP: SUBMITTED */}
          {step === "submitted" && (
            <div data-testid="step-submitted">
              <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#93C065] md:text-xs">
                Submitted
              </div>
              <h2 className="font-display mt-2 text-2xl font-black uppercase leading-[0.95] tracking-tight sm:text-3xl md:text-4xl">
                Got it. Studio&rsquo;s on it.
              </h2>
              <div className="mt-6 flex items-start gap-4 border-2 border-[#1A3626] bg-white p-5">
                <CheckCircle2
                  className="mt-1 h-6 w-6 shrink-0 text-[#93C065]"
                  strokeWidth={2.5}
                />
                <div className="text-sm leading-relaxed md:text-[15px]">
                  <p>
                    We&rsquo;ll hand-craft your personalised proof and email it
                    to{" "}
                    <span className="font-display font-black">{email}</span>{" "}
                    within 24 hours.
                  </p>
                  <p className="mt-3">
                    Once you&rsquo;ve seen the proof and you&rsquo;re happy,
                    you&rsquo;ll get a link to approve + check out. Nothing is
                    charged until then.
                  </p>
                </div>
              </div>
              <p className="mt-4 text-xs leading-relaxed text-[#1A3626]/70">
                Keep an eye on your inbox (and spam folder, just in case). If
                you don&rsquo;t hear back within 24 hours, reply to your
                confirmation email and we&rsquo;ll chase it.
              </p>
              <button
                onClick={onClose}
                className="wc-btn mt-7 w-full"
                data-testid="close-submitted-btn"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { PersonalisationModal };
